import api from '@/services/api';
import { vehiclesPath } from '.';
import type {
    Vehicle,
    VehicleStatus,
    VinVrmSearchInput,
    VinVrmSearchResponse,
} from '../types/vinvrmsearch.types';

type RecordValue = Record<string, unknown>;
type VehiclePayload = Vehicle & RecordValue;
type DerivativePayload = NonNullable<Vehicle['derivative']> & RecordValue;
type MakePayload = NonNullable<NonNullable<Vehicle['derivative']>['make']>;
type ModelPayload = NonNullable<NonNullable<Vehicle['derivative']>['model']>;
type EngineSizePayload = NonNullable<NonNullable<Vehicle['derivative']>['engine_size']>;
type TransmissionPayload = NonNullable<NonNullable<Vehicle['derivative']>['transmission']>;
type FuelTypePayload = NonNullable<NonNullable<Vehicle['derivative']>['fuel_type']>;

const asRecord = (value: unknown): RecordValue => {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as RecordValue)
        : {};
};

const section = (data: RecordValue, keys: string[]): RecordValue => {
    for (const key of keys) {
        const value = data[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return value as RecordValue;
        }
    }

    return {};
};

const stringValue = (sources: RecordValue[], keys: string[]): string | undefined => {
    for (const source of sources) {
        for (const key of keys) {
            const value = source[key];
            if (typeof value === 'string' && value.trim() !== '') {
                return value;
            }
            if (typeof value === 'number') {
                return String(value);
            }
        }
    }

    return undefined;
};

const numberValue = (sources: RecordValue[], keys: string[]): number | undefined => {
    const value = stringValue(sources, keys);
    if (value === undefined) {
        return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const makeObject = (existing: unknown, fallbackName?: string): MakePayload | null => {
    const record = asRecord(existing);
    const name = stringValue([record], ['name', 'Name', 'Make']) ?? fallbackName;

    if (!name) {
        return null;
    }

    return {
        id: (record.id as string | null | undefined) ?? null,
        name,
        code: stringValue([record], ['code', 'Code']) ?? null,
    };
};

const modelObject = (existing: unknown, fallbackName?: string): ModelPayload | null => {
    const record = asRecord(existing);
    const name = stringValue([record], ['name', 'Name', 'Model']) ?? fallbackName;

    if (!name) {
        return null;
    }

    const code = stringValue([record], ['code', 'Code']);

    return {
        id: (record.id as string | null | undefined) ?? null,
        name,
        ...(code ? { code } : {}),
    };
};

const capItem = (node: RecordValue): RecordValue => {
    const capIds = asRecord(node['CapIDs'] ?? node['CapIds']);
    const item = capIds['CapIDItem'];
    return asRecord(Array.isArray(item) ? item[0] : item);
};

// Mirrors CapIdentifier::idOrNull() - an integer or digit-only string, above zero - plus a
// safe-integer guard, since Number() would round a long digit string or overflow it to Infinity.
const capIdOrUndefined = (value: unknown): number | undefined => {
    const isDigitString = typeof value === 'string' && /^[0-9]+$/.test(value);
    if (!Number.isInteger(value) && !isDigitString) {
        return undefined;
    }

    const capId = Number(value);
    return Number.isSafeInteger(capId) && capId > 0 ? capId : undefined;
};

// Mirrors CapIdentifier::codeOrNull(): a non-string is not a code, and the stored value is trimmed.
const capCodeOrUndefined = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmed = value.trim();
    return trimmed !== '' ? trimmed : undefined;
};

const capValue = <T>(
    sources: RecordValue[],
    keys: string[],
    parse: (value: unknown) => T | undefined
): T | undefined => {
    for (const source of sources) {
        for (const key of keys) {
            const parsed = parse(source[key]);
            if (parsed !== undefined) {
                return parsed;
            }
        }
    }

    return undefined;
};

// Mirrors CarwebLookupService::vehicleNode() step for step: Vehicles.Vehicle, then the wrapper,
// then the payload root's own keys, then the root itself for shapes where raw already is the node.
const vehicleNode = (base: RecordValue, raw: RecordValue): RecordValue => {
    const collection = section(base, ['Vehicles', 'vehicles']);
    const node =
        collection['Vehicle'] ??
        collection['vehicle'] ??
        base['Vehicle'] ??
        base['vehicle'] ??
        raw['Vehicle'] ??
        raw['vehicle'] ??
        raw;

    return asRecord(Array.isArray(node) ? node[0] : node);
};

const hasDerivativeInfo = (derivative: DerivativePayload): boolean => {
    return Boolean(
        derivative.short_name ||
            derivative.description ||
            derivative.first_year_of_manufacture ||
            derivative.make ||
            derivative.model ||
            derivative.engine_size ||
            derivative.transmission ||
            derivative.fuel_type
    );
};

const normaliseVinVrmResponse = (data: VinVrmSearchResponse): VinVrmSearchResponse => {
    const vehicle = data.vehicle as VehiclePayload;
    const raw = asRecord(data.raw);
    const rawPayload = section(raw, ['DataArea', 'data_area', 'Response', 'response']);
    const rawBase = Object.keys(rawPayload).length > 0 ? rawPayload : raw;
    const rawVehicle = vehicleNode(rawBase, raw);
    const rawDerivative = section(rawBase, ['Derivative', 'derivative']);
    const sourceVehicle = asRecord(data.vehicle);
    const sourceDerivative = asRecord(data.derivative ?? vehicle.derivative);
    const rawCapItem = capItem(rawVehicle);

    const makeName = stringValue(
        [sourceVehicle, rawVehicle],
        ['make', 'Make', 'DVLAMake', 'DvlaMake']
    );
    const modelName = stringValue(
        [sourceVehicle, rawVehicle],
        ['model', 'Model', 'DVLAModel', 'DvlaModel']
    );
    const engineCapacity = numberValue(
        [sourceVehicle, rawVehicle],
        [
            'engine_capacity_cc',
            'EngineCapacity',
            'EngineCapacityCC',
            'EngineSizeCC',
            'CylinderCapacity',
        ]
    );
    const transmission = stringValue(
        [sourceVehicle, rawVehicle],
        ['transmission', 'Transmission', 'TransmissionType']
    );
    const fuelType = stringValue([sourceVehicle, rawVehicle], ['fuel_type', 'FuelType', 'Fuel']);

    const derivative: DerivativePayload = {
        ...(sourceDerivative as DerivativePayload),
        type: 'derivative',
        cap_id: capValue(
            [sourceDerivative, sourceVehicle, rawCapItem],
            ['cap_id', 'CapID'],
            capIdOrUndefined
        ),
        cap_code: capValue(
            [sourceDerivative, sourceVehicle, rawCapItem],
            ['cap_code', 'CapCode'],
            capCodeOrUndefined
        ),
        short_name:
            stringValue(
                [sourceDerivative, sourceVehicle, rawDerivative, rawVehicle],
                [
                    'short_name',
                    'ShortName',
                    'DerivativeShortName',
                    'derivative_name',
                    'DerivativeName',
                    'Derivative',
                ]
            ) ?? (sourceDerivative.short_name as string | undefined),
        description:
            stringValue(
                [sourceDerivative, sourceVehicle, rawDerivative, rawVehicle],
                [
                    'description',
                    'Description',
                    'DerivativeDescription',
                    'derivative_name',
                    'DerivativeName',
                    'Derivative',
                ]
            ) ?? (sourceDerivative.description as string | undefined),
        first_year_of_manufacture:
            numberValue(
                [sourceDerivative, rawDerivative],
                ['first_year_of_manufacture', 'year_from', 'YearFrom', 'IntroducedYear']
            ) ?? (sourceDerivative.first_year_of_manufacture as number | undefined),
        make: makeObject(sourceDerivative.make, makeName),
        model: modelObject(sourceDerivative.model, modelName),
        engine_size:
            (sourceDerivative.engine_size as EngineSizePayload | undefined) ??
            (engineCapacity
                ? { id: null, size: engineCapacity, description: `${engineCapacity}cc` }
                : undefined),
        transmission:
            (sourceDerivative.transmission as TransmissionPayload | undefined) ??
            (transmission
                ? { id: null, type: transmission, description: transmission, speeds: null }
                : undefined),
        fuel_type:
            (sourceDerivative.fuel_type as FuelTypePayload | undefined) ??
            (fuelType
                ? { id: null, name: fuelType, code: null, description: fuelType }
                : undefined),
    };

    vehicle.type = 'vehicle';
    if (data.current_status) {
        vehicle.current_status = data.current_status;
    }
    if (hasDerivativeInfo(derivative)) {
        vehicle.derivative = derivative;
        data.derivative = derivative;
    }

    data.vehicle = vehicle;

    return data;
};

// A known vehicle contributes identity only; readings come from the operator or the MOT lookup.
const stripStoredReadings = (data: VinVrmSearchResponse): VinVrmSearchResponse => {
    if (data.source !== 'local' || !data.vehicle) {
        return data;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { last_mot_date: _lastMot, ...vehicle } = data.vehicle;
    data.vehicle = vehicle;
    if (data.current_status) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { mileage: _mileage, mileage_recorded_date: _recorded, ...status } = data.current_status;
        data.current_status = status;
        data.vehicle.current_status = status;
    }
    return data;
};

export const VINVRMSearch = async (
    searchParams: VinVrmSearchInput
): Promise<VinVrmSearchResponse> => {
    const response = await api.post(`${vehiclesPath}/search`, searchParams);
    const data = response.data.data as VinVrmSearchResponse;

    if (data.vehicle && data.derivative) {
        data.vehicle.derivative = data.derivative;
    }
    if (data.vehicle && data.current_status) {
        data.vehicle.current_status = data.current_status as VehicleStatus;
    }
    // Normalise the discriminator so isVehicle() (and getVehicleDisplayInfo()) recognise the
    // returned object as a Vehicle. The backend resource does not stamp `type`, but the
    // frontend type guard requires it. Stamping at the service boundary is the single point
    // of normalisation rather than loosening the guard, which would also accept
    // VehicleDerivative inputs.
    if (data.vehicle) {
        data.vehicle.type = 'vehicle';
        if (data.vehicle.derivative) {
            data.vehicle.derivative.type = 'derivative';
        }
    }
    return normaliseVinVrmResponse(stripStoredReadings(data));
};
